import fs from 'fs';
import path from 'path';

import {
  addAffixes,
  fillPlaceholderData,
  NODE_TYPE_DEFAULT,
  resolve,
} from '@wikibonsai/almanac';
import type { DocOpts, DocOptsIndex, DocTypeData } from '@wikibonsai/almanac';
import chalk from 'chalk';

import { MD } from '../util/const';
import { getConfig, getDocTypes } from '../util/config';
import { resetGlobCache } from '../util/util';
import type { AttrFormat, BuildNoteOpts } from '../util/builder';
import {
  buildNoteContent,
  cliBody,
  dirFromDocPath,
  mergeDocOpts,
  optsDifferFromBase,
  parseCliAttrs,
  shouldEmitTypeFrontmatter,
} from '../util/builder';


export function add(concept: string, argv?: any): void {
  const cwd: string = process.cwd();
  // read attr format from config (default: caml)
  const configUri: string = argv?.config || path.join(cwd, 'config.toml');
  const cfg: any = getConfig(configUri);
  const attrFormat: AttrFormat = (argv?.format === 'yaml' || cfg?.format?.attrs === 'yaml') ? 'yaml' : 'caml';
  const noteOpts: BuildNoteOpts = {
    format: attrFormat,
    camlPrefix: cfg?.format?.caml_prefix ?? true,
    camlSpace: cfg?.format?.caml_space ?? 'pretty',
    camlList: cfg?.format?.caml_list ?? 'mkdn',
  };
  // note: argv.doctype is unreliable here due to alias collision with --type;
  // always resolve from cwd directly
  const resolvedDocPath: string = path.resolve(cwd, './t.doc.toml');

  const hasPathOverride: boolean = argv?.path !== undefined && argv.path !== '';
  const hasPrefixOverride: boolean = argv?.prefix !== undefined && argv.prefix !== '';

  // argv.type may contain a file path from the global --doctype alias; filter it out
  const rawType: string | undefined = (typeof argv?.type === 'string' && argv.type.trim().length > 0)
    ? argv.type.trim()
    : undefined;
  const declaredType: string | undefined = (rawType && !rawType.includes('/') && !rawType.includes('.toml') && !rawType.includes('.yaml'))
    ? rawType
    : undefined;

  /** No file => doctypes are off: no load, no inference, no `--type`. */
  const doctypeManifestExists: boolean = fs.existsSync(resolvedDocPath);
  if (declaredType && !doctypeManifestExists) {
    const displayPath: string = path.relative(cwd, resolvedDocPath) || 't.doc.toml';
    console.error(chalk.red(
      `doctype manifest not found (${displayPath}); doctypes are not in use. Omit --type or create that file.`,
    ));
    return;
  }

  let docData: DocTypeData = {};
  if (doctypeManifestExists) {
    docData = getDocTypes(resolvedDocPath) as DocTypeData;
  }

  let typeName: string | undefined = declaredType;
  if (!typeName && doctypeManifestExists && docData.types && Object.keys(docData.types).length > 0) {
    const idx: DocOptsIndex = docData.types;
    const r: string = resolve(idx, concept, undefined, cwd, undefined, 'type');
    if (r !== NODE_TYPE_DEFAULT) {
      typeName = r;
    }
  }

  let extraFm: Record<string, unknown>;
  try {
    extraFm = parseCliAttrs(argv?.attr);
  } catch (e: any) {
    console.error(chalk.red(`could not parse --attr: ${e.message ?? e}`));
    return;
  }
  const prose: string = cliBody(argv);

  if (!typeName && (hasPathOverride || hasPrefixOverride)) {
    const pathStr: string = (argv?.path as string | undefined) ?? '/';
    const mergedPrefix: string | undefined = argv?.prefix as string | undefined;
    const dir: string = dirFromDocPath(pathStr, cwd);
    fs.mkdirSync(dir, { recursive: true });
    const prefixFilled: string | undefined = mergedPrefix !== undefined
      ? fillPlaceholderData(mergedPrefix, { date: new Date(), id: '' })
      : undefined;
    const basename: string = addAffixes(concept, prefixFilled, undefined);
    const fp: string = path.join(dir, basename + MD);
    if (fs.existsSync(fp)) {
      console.error(chalk.red(`concept file already exists: ${path.relative(cwd, fp)}`));
      return;
    }
    const content: string = buildNoteContent(extraFm, prose, noteOpts);
    fs.writeFileSync(fp, content, 'utf8');
    resetGlobCache();
    console.log(`created ${path.relative(cwd, fp)}`);
    return;
  }

  if (!typeName) {
    const fp: string = path.join(cwd, concept + MD);
    if (fs.existsSync(fp)) {
      console.error(chalk.red(`concept file already exists: ${concept}${MD}`));
      return;
    }
    const content: string = buildNoteContent(extraFm, prose, noteOpts);
    fs.writeFileSync(fp, content, 'utf8');
    resetGlobCache();
    console.log(`created ${path.relative(cwd, fp)}`);
    return;
  }

  const base: DocOpts | undefined = docData.types?.[typeName];
  if (!base) {
    const displayPath: string = path.relative(cwd, resolvedDocPath) || 't.doc.toml';
    console.error(chalk.red(`unknown document type "${typeName}" in ${displayPath}`));
    return;
  }

  const merged: DocOpts = mergeDocOpts(base, argv);
  if (optsDifferFromBase(base, merged)) {
    console.log(chalk.dim('note: CLI overrides differ from doctype defaults for this type'));
  }
  const attrKey: string = 'type';

  const prefixFilled: string | undefined = merged.prefix !== undefined
    ? fillPlaceholderData(merged.prefix, { date: new Date(), id: '' })
    : undefined;
  const suffixFilled: string | undefined = merged.suffix !== undefined
    ? fillPlaceholderData(merged.suffix, { date: new Date(), id: '' })
    : undefined;
  const basename: string = addAffixes(concept, prefixFilled, suffixFilled);
  const dir: string = dirFromDocPath(merged.path, cwd);
  fs.mkdirSync(dir, { recursive: true });
  const fp: string = path.join(dir, basename + MD);

  if (fs.existsSync(fp)) {
    console.error(chalk.red(`concept file already exists: ${path.relative(cwd, fp)}`));
    return;
  }

  const fm: Record<string, unknown> = { ...extraFm };
  if (shouldEmitTypeFrontmatter(merged)) {
    fm[attrKey] = typeName;
  }
  const content: string = buildNoteContent(fm, prose, noteOpts);
  fs.writeFileSync(fp, content, 'utf8');
  resetGlobCache();
  console.log(`created ${path.relative(cwd, fp)}`);
}
